import { syncService } from '../services/sync';
import { supabaseService } from '../db/supabase';
import { UIComponents } from '../ui/components';
import type { AppState, LocalMapeado, BemPatrimonial } from '../types';

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

export class ModuloInventario {
  constructor(private state: AppState) {}

  // ========== LOCALIZAÇÃO ==========
  inicializar() {
    this.prepararLocalizacao();
    this.vincularEventos();
    // Expor a função de teste no escopo global
    (window as any).testInventario = this.preencherParaTeste.bind(this);
  }

  private prepararLocalizacao() {
    const sedeWrap = byId('sede-wrap');
    const sedeGrid = byId('sede-grid');
    const blocoGrid = byId('bloco-grid');
    const info = byId('sala-descricao');

    sedeGrid.innerHTML = '';
    blocoGrid.innerHTML = '';

    if (!this.state.locaisDb.length) {
      this.state.bloco = null;
      this.state.andar = null;
      this.state.sala = null;

      sedeWrap?.classList.remove('hidden');

      if (info) {
        info.textContent = 'Nenhum local encontrado para este câmpus.';
      }

      const selectAndar = byId<HTMLSelectElement>('select-andar');
      const selectSala = byId<HTMLSelectElement>('select-sala');

      if (selectAndar) selectAndar.innerHTML = '<option>Sem andares mapeados</option>';
      if (selectSala) selectSala.innerHTML = '<option>Sem salas mapeadas</option>';

      return;
    }

    sedeWrap?.classList.remove('hidden');
    const sedes = this.getSedesPorCampus();

    if (!sedes.length) {
      this.state.sede = null;
    } else if (!sedes.includes(String(this.state.sede))) {
      this.state.sede = sedes[0];
    }

    sedes.forEach((sedeId) => {
      const nome = (this.state.campus as any)?.sedes?.[sedeId]?.nome ||
                   (sedeId.charAt(0).toUpperCase() + sedeId.slice(1));

      const card = this.criarCard(
        nome,
        '',
        () => {
          this.state.sede = sedeId;
          this.state.bloco = null;
          this.renderizarSedesAtivas();
          this.renderizarBlocos();
        },
        this.state.sede === sedeId
      );

      sedeGrid?.appendChild(card);
    });

    this.renderizarSedesAtivas();
    this.renderizarBlocos();
  }

  private getSedesPorCampus(): string[] {
    return [...new Set(
      this.state.locaisDb
        .map(l => l.sede)
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));
  }

  private getBlocosPorCampus(): string[] {
    const locaisFiltrados = this.state.locaisDb.filter(l =>
      !this.state.sede || l.sede === this.state.sede
    );

    return [...new Set(
      locaisFiltrados
        .map(l => l.bloco)
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));
  }

  private renderizarSedesAtivas() {
    document.querySelectorAll('#sede-grid .card').forEach(el =>
      el.classList.remove('active-choice')
    );

    const sedes = this.getSedesPorCampus();
    const idx = sedes.indexOf(String(this.state.sede));

    if (idx >= 0) {
      const cards = document.querySelectorAll('#sede-grid .card');
      cards[idx]?.classList.add('active-choice');
    }
  }

  private renderizarBlocos() {
    const blocoGrid = byId('bloco-grid');
    if (!blocoGrid) return;

    blocoGrid.innerHTML = '';
    const blocos = this.getBlocosPorCampus();

    blocos.forEach(blocoNome => {
      const localInfo = this.state.locaisDb.find(l =>
        l.bloco === blocoNome &&
        (!this.state.sede || l.sede === this.state.sede)
      );

      const desc = localInfo?.descricaoBloco || '';
      const displayText = desc ? `${blocoNome} - ${desc}` : blocoNome;

      const card = this.criarCard(
        displayText,
        '',
        () => {
          this.state.bloco = blocoNome;
          this.atualizarBlocoSelecionadoUI();
          this.renderizarAndares();
        },
        this.state.bloco === blocoNome
      );

      blocoGrid.appendChild(card);
    });

    if (!this.state.bloco && blocos.length) {
      this.state.bloco = blocos[0];
      this.renderizarAndares();
      this.atualizarBlocoSelecionadoUI();
    }
  }

  private atualizarBlocoSelecionadoUI() {
    document.querySelectorAll('#bloco-grid .card').forEach(el =>
      el.classList.remove('active-choice')
    );

    const blocos = this.getBlocosPorCampus();
    const idx = blocos.indexOf(String(this.state.bloco));

    if (idx >= 0) {
      const cards = document.querySelectorAll('#bloco-grid .card');
      cards[idx]?.classList.add('active-choice');
    }
  }

  private renderizarAndares() {
    const selectAndar = byId<HTMLSelectElement>('select-andar');
    const selectSala = byId<HTMLSelectElement>('select-sala');
    const info = byId('sala-descricao');

    if (!selectAndar || !selectSala || !info) return;

    const locais = this.state.locaisDb.filter(l =>
      (!this.state.sede || l.sede === this.state.sede) &&
      l.bloco === this.state.bloco
    );

    const ordenarAndares = (valor: string | null | undefined): number => {
      if (!valor) return 999;
      const texto = valor.toString().trim().toUpperCase();
      if (!texto) return 999;

      if (texto.startsWith('S')) {
        const numerosSubsolo = texto.replace(/[^0-9-]/g, '');
        const parsed = numerosSubsolo ? parseInt(numerosSubsolo, 10) : 1;
        return -Math.abs(parsed || 1);
      }

      const somenteDigitos = texto.replace(/[^0-9]/g, '');
      if (somenteDigitos) {
        const parsed = parseInt(somenteDigitos, 10);
        if (!Number.isNaN(parsed)) return parsed;
      }

      if (texto.includes('TER')) return 0;
      if (texto.includes('MEZ')) return 1;

      return 999;
    };

    const normalizarAndar = (valor: string | null | undefined): string => {
      if (!valor) return '';
      return valor.normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
    };

    const mapaAndares = new Map<string, string>();
    locais.forEach(l => {
      const raw = l.andar ?? '';
      const normalizado = normalizarAndar(raw);
      if (normalizado && !mapaAndares.has(normalizado)) {
        mapaAndares.set(normalizado, raw);
      }
    });

    const listaAndares = Array.from(mapaAndares.entries())
      .map(([normalizado, raw]) => ({ normalizado, raw }))
      .sort((a, b) => ordenarAndares(a.normalizado) - ordenarAndares(b.normalizado));

    selectAndar.innerHTML = '';

    if (!listaAndares.length) {
      selectAndar.add(new Option('Sem andares mapeados', ''));
      selectSala.innerHTML = '<option>Sem salas mapeadas</option>';
      this.state.andar = null;
      this.state.sala = null;
      this.state.salaSetor = '';
      this.state.salaDescricao = '';
      info.textContent = 'Esse bloco ainda não possui salas mapeadas.';
      return;
    }

    listaAndares.forEach(({ raw: andarRaw }) => {
      let andarDisplay = String(andarRaw);
      if (andarDisplay.match(/\bAndar$/i)) {
        andarDisplay = andarDisplay.replace(/\s*Andar$/i, '').trim();
      }
      if (!andarDisplay) andarDisplay = String(andarRaw);
      selectAndar.add(new Option(andarDisplay, String(andarRaw)));
    });

    const alvoNormalizado = normalizarAndar(this.state.andar);
    const andarSelecionado = listaAndares.find(item => item.normalizado === alvoNormalizado)
      ?? listaAndares[0];

    const valorAndar = andarSelecionado?.raw ?? listaAndares[0].raw;

    selectAndar.value = String(valorAndar);
    this.state.andar = String(valorAndar);

    selectAndar.onchange = () => {
      this.state.andar = selectAndar.value;
      this.renderizarSalas();
    };

    this.renderizarSalas();
  }

  private renderizarSalas() {
    const selectSala = byId<HTMLSelectElement>('select-sala');
    const info = byId('sala-descricao');

    if (!selectSala || !info) return;

    const normalizar = (valor: string | null | undefined): string => {
      if (!valor) return '';
      return valor.toString().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toUpperCase();
    };

    const alvoAndarNormalizado = normalizar(this.state.andar);

    const salas = this.state.locaisDb
      .filter(l =>
        (!this.state.sede || l.sede === this.state.sede) &&
        l.bloco === this.state.bloco &&
        normalizar(l.andar) === alvoAndarNormalizado
      )
      .sort((a, b) => a.sala.localeCompare(b.sala));

    selectSala.innerHTML = '';

    salas.forEach(s =>
      selectSala.add(new Option(s.sala, s.sala))
    );

    if (!salas.length) {
      this.state.sala = null;
      this.state.salaSetor = '';
      this.state.salaDescricao = '';
      info.textContent = 'Sem salas nesse andar ainda.';
      return;
    }

    const setAtual = (loc: LocalMapeado) => {
      this.state.sala = loc.sala;
      this.state.salaSetor = loc.setor || '';
      this.state.salaDescricao = loc.descricao || '';

      const setorDescricao = [this.state.salaSetor, this.state.salaDescricao]
        .filter(Boolean)
        .join(' ');

      this.preencherResumoLocal(
        info,
        this.codigoLocal(this.state.bloco!, this.state.andar!, this.state.sala!),
        setorDescricao
      );
    };

    const salaAlvoNormalizada = normalizar(this.state.sala);
    const salaAtual =
      salas.find(s => s.sala === this.state.sala) ??
      salas.find(s => normalizar(s.sala) === salaAlvoNormalizada && (s.setor || s.descricao)) ??
      salas.find(s => normalizar(s.sala) === salaAlvoNormalizada) ??
      salas[0];

    selectSala.value = salaAtual.sala;
    setAtual(salaAtual);

    selectSala.onchange = () => {
      const salaEncontrada = salas.find(s => s.sala === selectSala.value);
      if (salaEncontrada) setAtual(salaEncontrada);
    };
  }

  private codigoLocal(bloco: string, andar: string, sala: string): string {
    const blocoTxt = String(bloco || '').trim();
    let andarNum = String(andar || '').trim();
    andarNum = andarNum.replace(/andar/gi, '');
    andarNum = andarNum.replace(/[^0-9]/g, '');
    const salaTxt = String(sala || '').trim();
    return `${blocoTxt}${andarNum}${salaTxt}`;
  }

  private preencherResumoLocal(container: HTMLElement | null, codigo: string, descricao?: string) {
    if (!container) return;

    const descricaoFormatada = (descricao || '').trim();
    const textoDescricao = descricaoFormatada || '-';
    const temDescricao = textoDescricao !== '-' && textoDescricao.length > 0;

    container.innerHTML = '';

    const wrapper = document.createElement('span');
    wrapper.className = 'local-summary-content';

    const codigoEl = document.createElement('strong');
    codigoEl.className = 'local-summary-code';
    codigoEl.textContent = codigo;
    wrapper.appendChild(codigoEl);

    if (temDescricao) {
      const separador = document.createElement('span');
      separador.className = 'local-summary-separator';
      separador.textContent = '•';
      wrapper.appendChild(separador);
    }

    const descricaoEl = document.createElement('span');
    descricaoEl.className = 'local-summary-text';
    descricaoEl.textContent = textoDescricao;
    wrapper.appendChild(descricaoEl);

    container.appendChild(wrapper);
  }

  // ========== INVENTÁRIO DO LOCAL ==========
  entrarInventarioLocal() {
    if (!this.state.bloco || !this.state.sala) {
      UIComponents.error('Selecione bloco, andar e sala antes de continuar.');
      return;
    }

    // Limpa o formulário sempre que um novo local é selecionado
    this.limparFormulario();

    const localSelecionado = byId('local-selecionado');
    if (localSelecionado) {
      const setorDescricao = [this.state.salaSetor, this.state.salaDescricao]
        .filter(Boolean)
        .join(' ');

      this.preencherResumoLocal(
        localSelecionado,
        this.codigoLocal(this.state.bloco, this.state.andar!, this.state.sala),
        setorDescricao
      );
    }

    const url = `#${this.state.campus?.id}/inventario/${this.state.bloco}/${this.state.andar}/${this.state.sala}`;
    history.pushState({}, '', url);

    const event = new CustomEvent('app:show-view', { detail: 'view-inventario' });
    window.dispatchEvent(event);
  }

  voltarParaLocalizacao() {
    const url = `#${this.state.campus?.id}/inventario`;
    history.pushState({}, '', url);
    const event = new CustomEvent('app:show-view', { detail: 'view-localizacao' });
    window.dispatchEvent(event);
  }

  // ========== BUSCAR BEM (PÚBLICO) ==========
  public async buscarBemPorTombo() {
    const inputTombo = byId<HTMLInputElement>('input-tombo');
    const inputDescricao = byId<HTMLInputElement>('input-descricao');
    const resultadoBusca = byId('resultado-busca');

    if (!inputTombo || !inputDescricao || !resultadoBusca) return;

    const tombo = inputTombo.value.trim();
    console.log(`[DEBUG] Buscando tombo: "${tombo}" (tipo: ${typeof tombo})`);

    if (!tombo) {
      UIComponents.warning('Informe o número do bem.');
      return;
    }

    resultadoBusca.textContent = 'Consultando base patrimonial...';

    try {
      const bem = await syncService.buscarBemPorTombo(tombo);

      if (!bem) {
        this.state.bemBase = null;
        resultadoBusca.textContent = 'Bem não localizado na base.';
        console.warn(`[DEBUG] Tombo "${tombo}" não encontrado.`);
        return;
      }

      console.log('[DEBUG] Bem encontrado:', bem);

      const descricaoComposta = [
        (bem as any).descricao,
        (bem as any).caracteristica
      ].filter(Boolean).join(' - ');

      inputDescricao.value = descricaoComposta;

      const campusOrigem = (bem as any).campus_origem || '-';
      const campusDivergente = campusOrigem !== '-' && campusOrigem !== this.state.campus?.id;
      const campusHtml = campusDivergente
        ? `${campusOrigem} <span class="campus-origem-alert">⚠ Bem de outro câmpus!</span>`
        : campusOrigem;

      resultadoBusca.innerHTML = `
        <strong>Bem localizado:</strong> ${descricaoComposta || '-'}<br>
        <small>Tombo: ${tombo}</small><br>
        <small>Campus: ${campusHtml}</small><br>
        <small>Setor: ${(bem as any).setor_sigla || '-'}${(bem as any).setor_descricao ? ` · ${(bem as any).setor_descricao}` : ''}</small><br>
        <small>Local físico: ${(bem as any).local_fisico || '-'}</small>
      `;

      this.state.bemBase = bem;

    } catch (error) {
      console.error('Erro ao buscar bem:', error);
      resultadoBusca.textContent = `Erro na consulta: ${(error as Error).message}`;
    }
  }

  // ========== REGISTRAR BEM ==========
  async registrarBem() {
    const inputTombo = byId<HTMLInputElement>('input-tombo');
    const inputDescricao = byId<HTMLInputElement>('input-descricao');
    const inputDescricaoCorreta = byId<HTMLInputElement>('input-descricao-correta');
    const inputObservacoes = byId<HTMLTextAreaElement>('input-observacoes');
    const inputFoto = byId<HTMLInputElement>('input-foto');

    if (!inputTombo || !inputDescricao) return;

    const tombo = this.state.semTombo ? '0' : inputTombo.value.trim();
    const descricao = inputDescricao.value.trim();
    const descricaoCorreta = inputDescricaoCorreta?.value.trim() || '';
    const observacoes = inputObservacoes?.value.trim() || '';
    const fotoArquivo = inputFoto?.files?.[0];

    if (!this.state.sala) {
      UIComponents.error('Selecione o local antes de inventariar.');
      return;
    }
    if (!descricao) {
      UIComponents.error('Informe a descrição do bem.');
      return;
    }
    if (!this.state.semTombo && !tombo) {
      UIComponents.error('Informe o tombo ou marque "Bem sem Tombo".');
      return;
    }
    if (this.state.semTombo && !fotoArquivo) {
      UIComponents.error('Foto obrigatória para bem sem tombo.');
      return;
    }
    if (this.state.descricaoNaoConfere && !descricaoCorreta) {
      UIComponents.error('Informe a descrição correta quando não confere.');
      return;
    }

    const loading = UIComponents.showLoading('Registrando bem...');

    try {
      let fotoUrl: string | null = null;
      if (fotoArquivo) {
        console.log('[DEBUG] Iniciando upload da foto...');
        try {
          const path = `${this.state.campus!.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`;
          fotoUrl = await supabaseService.uploadFoto(fotoArquivo, path);
          console.log(`[DEBUG] Foto enviada com sucesso. URL: ${fotoUrl}`);
        } catch (uploadError) {
          console.error('❌ Erro específico no upload da foto:', uploadError);
          throw new Error('Falha ao enviar a foto.');
        }
      }

      const payload: BemPatrimonial = {
        campus: this.state.campus!.id,
        sede: this.state.sede || undefined,
        bloco: this.state.bloco || undefined,
        sala: this.state.sala || undefined,
        tombo,
        descricao,
        descricaoConfere: !this.state.descricaoNaoConfere,
        descricaoCorreta: this.state.descricaoNaoConfere ? descricaoCorreta : undefined,
        danificado: this.state.danificado,
        semTombo: this.state.semTombo,
        observacao: observacoes,
        foto: fotoUrl || undefined,
        localVerificacao: this.codigoLocal(
          this.state.bloco!,
          this.state.andar!,
          this.state.sala!
        ),
        timestamp: new Date().toISOString()
      };
      
      console.log('[DEBUG] Payload final para registro:', payload);

      await syncService.registrarBem(payload);

      UIComponents.hideLoading(loading);
      UIComponents.success('Bem registrado com sucesso!');
      this.limparFormulario();

    } catch (error) {
      console.error('Erro ao registrar bem:', error);
      UIComponents.hideLoading(loading);
      UIComponents.error((error as Error).message || 'Erro ao registrar bem. Tente novamente.');
    }
  }

  private limparFormulario() {
    const inputTombo = byId<HTMLInputElement>('input-tombo');
    const inputDescricao = byId<HTMLInputElement>('input-descricao');
    const inputDescricaoCorreta = byId<HTMLInputElement>('input-descricao-correta');
    const inputObservacoes = byId<HTMLTextAreaElement>('input-observacoes');
    const inputFoto = byId<HTMLInputElement>('input-foto');
    const resultadoBusca = byId('resultado-busca');
    const btnRemoverFoto = byId('btn-remover-foto');

    if (inputTombo) inputTombo.value = '';
    if (inputDescricao) inputDescricao.value = '';
    if (inputDescricaoCorreta) inputDescricaoCorreta.value = '';
    if (inputObservacoes) inputObservacoes.value = '';
    if (inputFoto) inputFoto.value = '';
    if (resultadoBusca) resultadoBusca.textContent = 'Nenhuma consulta ainda.';
    if (btnRemoverFoto) btnRemoverFoto.classList.add('hidden');

    this.state.semTombo = false;
    this.state.danificado = false;
    this.state.descricaoNaoConfere = false;
    this.state.bemBase = null;

    byId('btn-sem-tombo')?.classList.remove('active-choice');
    byId('btn-danificado')?.classList.remove('active-choice');
    byId('btn-descricao-nao-confere')?.classList.remove('active-choice');
    byId('field-descricao-correta')?.classList.add('hidden');
    
    if (inputTombo) {
      inputTombo.readOnly = false;
      inputTombo.placeholder = '';
      inputTombo.focus();
    }
    if (inputDescricao) {
      inputDescricao.readOnly = false;
      inputDescricao.placeholder = 'Descrição do bem (preenchida automaticamente)';
    }
  }

  private preencherParaTeste() {
    byId<HTMLInputElement>('input-tombo').value = '123456';
    byId<HTMLInputElement>('input-descricao').value = 'MESA ESCRITORIO C/ 3 GAVETAS';
    this.state.danificado = true;
    byId('btn-danificado')?.classList.add('active-choice');
    byId<HTMLTextAreaElement>('input-observacoes').value = 'Teste de preenchimento automático';
    console.log('Formulário preenchido para teste.');
  }

  // ========== HELPERS ==========
  private criarCard(
    titulo: string,
    descricao: string,
    onClick: () => void,
    ativo = false
  ): HTMLElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `card ${ativo ? 'active-choice' : ''}`;
    btn.innerHTML = `
      <strong>${titulo}</strong>
      ${descricao ? `<small>${descricao}</small>` : ''}
    `;
    btn.addEventListener('click', onClick);
    return btn;
  }

  // ========== EVENTOS ==========
  private vincularEventos() {
    byId('btn-inventariar-local')?.addEventListener('click', () => this.entrarInventarioLocal());
    byId('btn-trocar-local')?.addEventListener('click', () => this.voltarParaLocalizacao());
    byId('btn-buscar-tombo')?.addEventListener('click', () => this.buscarBemPorTombo());
    byId('btn-registrar')?.addEventListener('click', () => this.registrarBem());
    byId('btn-limpar-form')?.addEventListener('click', () => this.limparFormulario());

    const inputFoto = byId<HTMLInputElement>('input-foto');
    const btnRemoverFoto = byId('btn-remover-foto');

    inputFoto?.addEventListener('change', () => {
      btnRemoverFoto?.classList.toggle('hidden', !inputFoto.files?.length);
    });
    btnRemoverFoto?.addEventListener('click', () => {
      if (inputFoto) inputFoto.value = '';
      btnRemoverFoto.classList.add('hidden');
    });

    // Toggles
    byId('btn-sem-tombo')?.addEventListener('click', (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      this.state.semTombo = !this.state.semTombo;
      btn.classList.toggle('active-choice', this.state.semTombo);

      const inputTombo = byId<HTMLInputElement>('input-tombo');
      const inputDescricao = byId<HTMLInputElement>('input-descricao');

      if (this.state.semTombo) {
        if (inputTombo) { inputTombo.value = '0'; inputTombo.readOnly = true; }
        if (inputDescricao) { inputDescricao.readOnly = false; inputDescricao.placeholder = 'Digite a descrição do bem sem tombo'; inputDescricao.focus(); }
      } else {
        if (inputTombo) { inputTombo.value = ''; inputTombo.readOnly = false; }
        if (inputDescricao) { inputDescricao.placeholder = 'Descrição do bem (preenchida automaticamente)'; }
      }
    });

    byId('btn-danificado')?.addEventListener('click', (e) => {
      this.state.danificado = !this.state.danificado;
      (e.currentTarget as HTMLButtonElement).classList.toggle('active-choice', this.state.danificado);
    });

    byId('btn-descricao-nao-confere')?.addEventListener('click', (e) => {
      this.state.descricaoNaoConfere = !this.state.descricaoNaoConfere;
      (e.currentTarget as HTMLButtonElement).classList.toggle('active-choice', this.state.descricaoNaoConfere);
      byId('field-descricao-correta')?.classList.toggle('hidden', !this.state.descricaoNaoConfere);
    });
  }
}
